import type { CellTypeSignal } from "./types";
import { expectedTypeAt, type CatalogMap } from "./catalog";
import { addressFromRC } from "../rules/audit/addressing";

/**
 * Usage-based cell type inference. Walks a formula's character stream while
 * tracking the function-call stack; for every cell or range reference, looks
 * up the expected type for its argument position and emits a signal.
 *
 * Example: in `=EOMONTH(A1, 1)`, the parser pushes EOMONTH onto the stack,
 * sees A1 in argument position 0, looks up EOMONTH arg 0 = "date", and emits
 * `{ type: "date", source: "usage:EOMONTH:arg1", confidence: "medium" }` for
 * the address `Sheet!A1`.
 *
 * Handles:
 *   - Nested function calls (inner frame tracks its own arg index)
 *   - Quoted sheet names: 'My Sheet'!A1
 *   - Unquoted sheet names: Sheet1!A1
 *   - Ranges: A1:B10 (each cell gets the signal, up to MAX_RANGE_CELLS)
 *   - String literals: "..." (skipped, with "" -> escaped-quote handling)
 *   - Plain grouping parens (do NOT bump parent's arg index)
 *
 * Does NOT handle (silently skipped):
 *   - INDIRECT(text) — runtime-resolved references
 *   - Whole-column/row refs (A:A, 1:1)
 *   - 3D refs (Sheet1:Sheet5!A1)
 *   - Structured table refs: Table1[Col1]
 *   - Defined names
 */

interface Frame {
  /** Empty string for plain grouping parens. */
  funcName: string;
  argIndex: number;
}

interface RefMatch {
  sheet: string | null;
  /** Top-left of the range, e.g. "A1" (no $). */
  start: string;
  /** Bottom-right, equals start for a single cell. */
  endRef: string;
  /** Position in the formula just past the matched reference. */
  end: number;
}

// Cap how many cells a single range reference can fan signals out to. The
// signal is the SAME for every cell in the range (e.g., SUM(A1:A1000) means
// "all 1000 cells are numeric"), so the first N are sufficient to confirm
// the pattern. Larger ranges only inflate memory and serialization cost.
const MAX_RANGE_CELLS = 256;

export function inferFromFormula(
  formula: string,
  defaultSheet: string,
  out: Map<string, CellTypeSignal[]>,
  catalog?: CatalogMap
): void {
  if (!formula) return;
  const f = formula.startsWith("=") ? formula.slice(1) : formula;
  const len = f.length;

  const stack: Frame[] = [];
  let i = 0;

  while (i < len) {
    const c = f[i];

    // --- String literal: "..." with "" as escaped quote
    if (c === '"') {
      i = skipStringLiteral(f, i);
      continue;
    }

    // --- Possible quoted sheet reference, or stray quoted span we should skip
    if (c === "'") {
      const ref = matchRef(f, i);
      if (ref) {
        emitForRef(ref, stack, defaultSheet, out, catalog);
        i = ref.end;
        continue;
      }
      i = skipQuotedSpan(f, i);
      continue;
    }

    if (c === ",") {
      const top = stack[stack.length - 1];
      if (top) top.argIndex++;
      i++;
      continue;
    }

    if (c === "(") {
      // Plain grouping paren — commas inside still bump the OUTER frame's arg
      // index, but we want to isolate them, so push a no-name frame.
      stack.push({ funcName: "", argIndex: 0 });
      i++;
      continue;
    }

    if (c === ")") {
      stack.pop();
      i++;
      continue;
    }

    // --- Function call or reference (starts with letter/underscore)
    if (/[A-Za-z_]/.test(c)) {
      const fn = matchFuncCall(f, i);
      if (fn) {
        stack.push({ funcName: fn.name, argIndex: 0 });
        i = fn.end;
        continue;
      }
      const ref = matchRef(f, i);
      if (ref) {
        emitForRef(ref, stack, defaultSheet, out, catalog);
        i = ref.end;
        continue;
      }
    }

    // --- Absolute reference like $A$1 (no sheet prefix)
    if (c === "$") {
      const ref = matchRef(f, i);
      if (ref) {
        emitForRef(ref, stack, defaultSheet, out, catalog);
        i = ref.end;
        continue;
      }
    }

    i++;
  }
}

function skipStringLiteral(f: string, startIdx: number): number {
  let i = startIdx + 1;
  const len = f.length;
  while (i < len) {
    if (f[i] === '"') {
      if (f[i + 1] === '"') {
        i += 2; // escaped quote
        continue;
      }
      return i + 1;
    }
    i++;
  }
  return i;
}

function skipQuotedSpan(f: string, startIdx: number): number {
  let i = startIdx + 1;
  const len = f.length;
  while (i < len) {
    if (f[i] === "'") {
      if (f[i + 1] === "'") {
        i += 2;
        continue;
      }
      return i + 1;
    }
    i++;
  }
  return i;
}

function matchFuncCall(f: string, start: number): { name: string; end: number } | null {
  if (!/[A-Za-z_]/.test(f[start])) return null;
  let j = start + 1;
  while (j < f.length && /[A-Za-z0-9_.]/.test(f[j])) j++;
  // Allow whitespace between name and "("
  let k = j;
  while (k < f.length && /\s/.test(f[k])) k++;
  if (f[k] !== "(") return null;
  return { name: f.slice(start, j).toUpperCase(), end: k + 1 };
}

function matchRef(f: string, start: number): RefMatch | null {
  let i = start;
  let sheet: string | null = null;

  if (f[i] === "'") {
    let j = i + 1;
    // Quoted-sheet bodies can contain '' as an escaped quote
    while (j < f.length) {
      if (f[j] === "'") {
        if (f[j + 1] === "'") {
          j += 2;
          continue;
        }
        break;
      }
      j++;
    }
    if (f[j] !== "'" || f[j + 1] !== "!") return null;
    sheet = f.slice(i + 1, j).replace(/''/g, "'");
    i = j + 2;
  } else if (/[A-Za-z_]/.test(f[i])) {
    // Try unquoted sheet name followed by "!"
    let j = i;
    while (j < f.length && /[A-Za-z0-9_.]/.test(f[j])) j++;
    if (f[j] === "!") {
      sheet = f.slice(i, j);
      i = j + 1;
    }
  }

  // Cell ref: $?[A-Z]+$?\d+
  const cellRe = /\$?[A-Z]+\$?\d+/y;
  cellRe.lastIndex = i;
  const m = cellRe.exec(f);
  if (!m || m.index !== i) return null;
  const startCell = m[0].replace(/\$/g, "");
  i += m[0].length;

  let endCell = startCell;
  if (f[i] === ":") {
    const re2 = /\$?[A-Z]+\$?\d+/y;
    re2.lastIndex = i + 1;
    const m2 = re2.exec(f);
    if (m2 && m2.index === i + 1) {
      endCell = m2[0].replace(/\$/g, "");
      i += 1 + m2[0].length;
    } else {
      // "A1:" without a valid following cell (e.g., "A1:A") — leave as single
    }
  }

  return { sheet, start: startCell, endRef: endCell, end: i };
}

function emitForRef(
  ref: RefMatch,
  stack: Frame[],
  defaultSheet: string,
  out: Map<string, CellTypeSignal[]>,
  catalog?: CatalogMap
): void {
  // Find the innermost NAMED function frame. (Grouping parens shouldn't
  // shadow the enclosing function's type expectations.)
  let frame: Frame | null = null;
  for (let k = stack.length - 1; k >= 0; k--) {
    if (stack[k].funcName) {
      frame = stack[k];
      break;
    }
  }
  if (!frame) return;

  const expected = expectedTypeAt(frame.funcName, frame.argIndex, catalog);
  if (!expected || expected === "unknown") return;

  const sheet = ref.sheet ?? defaultSheet;
  const addrs = expandRange(ref.start, ref.endRef);
  if (addrs.length === 0) return;

  const source = `usage:${frame.funcName}:arg${frame.argIndex + 1}`;
  for (const local of addrs) {
    const key = `${sheet}!${local}`;
    let arr = out.get(key);
    if (!arr) {
      arr = [];
      out.set(key, arr);
    }
    arr.push({ type: expected, source, confidence: "medium" });
  }
}

function expandRange(startAddr: string, endAddr: string): string[] {
  const a = parseA1(startAddr);
  const b = parseA1(endAddr);
  if (!a || !b) return [];

  const r0 = Math.min(a.row, b.row);
  const r1 = Math.max(a.row, b.row);
  const c0 = Math.min(a.col, b.col);
  const c1 = Math.max(a.col, b.col);

  const total = (r1 - r0 + 1) * (c1 - c0 + 1);
  if (total <= 0 || total > MAX_RANGE_CELLS) return [];

  const out: string[] = [];
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      out.push(addressFromRC(r, c));
    }
  }
  return out;
}

function parseA1(addr: string): { row: number; col: number } | null {
  const m = /^([A-Z]+)(\d+)$/.exec(addr);
  if (!m) return null;
  let col = 0;
  for (const ch of m[1]) col = col * 26 + (ch.charCodeAt(0) - 64);
  return { row: parseInt(m[2], 10) - 1, col: col - 1 };
}
